<template>
  <div class="floating-chat-widget">
    <transition name="widget-pop">
      <section v-if="isOpen" class="chat-panel" aria-label="Chat widget">
        <header class="chat-panel-header">
          <div class="chat-title-wrap">
            <img src="/config/logo_zuri_short.png" alt="UPB" class="header-avatar" />
            <div>
              <h3 class="chat-title">ZURI AI - UPB Assistant</h3>
              <p class="chat-subtitle">Respuesta en tiempo real</p>
            </div>
          </div>
          <button class="close-btn" @click="isOpen = false" aria-label="Cerrar chat">
            x
          </button>
        </header>

        <div ref="messagesContainer" class="chat-messages">
          <article
            v-for="(message, index) in messages"
            :key="index"
            class="message"
            :class="message.sender"
          >
            <div class="bubble">
              <span v-if="message.sender === 'user'">{{ message.content }}</span>
              <div v-else>
                <div v-html="renderBotContent(message.content)"></div>
                <span v-if="message.isTyping" class="typing-cursor">|</span>
              </div>
            </div>
          </article>

          <div v-if="isLoading" class="typing-row">
            <span class="typing-dot"></span>
            <span class="typing-dot"></span>
            <span class="typing-dot"></span>
          </div>
        </div>

        <form class="chat-input-row" @submit.prevent="sendMessage">
          <input
            v-model="draft"
            type="text"
            class="chat-input"
            placeholder="Escribe tu pregunta"
            :disabled="isLoading || isStreaming"
          />
          <!-- Mic button: visible when input is empty -->
          <button
            v-if="!draft.trim()"
            type="button"
            class="action-btn mic-btn"
            :class="{ recording: isRecording, transcribing: isTranscribing }"
            @click="toggleRecording"
            :disabled="isLoading || isStreaming || isTranscribing"
            :title="isRecording ? 'Detener grabación' : isTranscribing ? 'Transcribiendo…' : 'Grabar voz'"
          >
            <i v-if="isTranscribing" class="fas fa-spinner fa-spin"></i>
            <i v-else-if="isRecording" class="fas fa-stop"></i>
            <i v-else class="fas fa-microphone"></i>
          </button>
          <!-- Send button: visible when input has text -->
          <button
            v-else
            type="submit"
            class="action-btn send-btn"
            :disabled="isLoading || isStreaming"
            title="Enviar mensaje"
          >
            <i class="fas fa-paper-plane"></i>
          </button>
        </form>
      </section>
    </transition>

    <button class="launcher-btn" @click="toggleWidget" :aria-label="isOpen ? 'Cerrar chat' : 'Abrir chat'">
      <img src="/config/logo_zuri_short.png" alt="ZURI" class="launcher-logo" />
      <span class="launcher-tooltip">{{ isOpen ? 'Cerrar' : 'Chat' }}</span>
    </button>
  </div>
</template>

<script>
import { marked } from "marked";
import DOMPurify from "dompurify";
import chatbotService from "@/services/chatbotService";

export default {
  name: "FloatingChatWidget",
  data() {
    return {
      isOpen: false,
      isLoading: false,
      isStreaming: false,
      draft: "",
      sessionId: null,
      messages: [],
      typewriterTimer: null,
      isRecording: false,
      isTranscribing: false,
      mediaRecorder: null,
      audioChunks: [],
    };
  },
  mounted() {
    this.seedWelcomeMessage();
  },
  beforeUnmount() {
    if (this.typewriterTimer) clearInterval(this.typewriterTimer);
  },
  methods: {
    seedWelcomeMessage() {
      if (this.messages.length > 0) return;
      this.messages.push({
        sender: "bot",
        content: this.getWelcomeMessage(),
        timestamp: new Date().toISOString(),
      });
    },
    getWelcomeMessage() {
      try {
        if (typeof this.$t === "function") {
          const translated = this.$t("chatbot.welcomeMessage");
          if (translated && translated !== "chatbot.welcomeMessage") {
            return translated;
          }
        }
      } catch (error) {
        console.warn("Could not read i18n welcome message:", error);
      }
      return this.$config?.features?.chat?.welcomeMessage ||
        "Hola, soy tu asistente virtual. Como puedo ayudarte hoy?";
    },
    toggleWidget() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.$nextTick(() => this.scrollToBottom());
      }
    },
    renderBotContent(content) {
      try {
        return DOMPurify.sanitize(marked.parse(content || ""));
      } catch (error) {
        console.error("Error rendering markdown:", error);
        return DOMPurify.sanitize(content || "");
      }
    },
    async sendMessage() {
      const content = this.draft.trim();
      if (!content || this.isLoading || this.isStreaming) return;

      this.messages.push({
        sender: "user",
        content,
        timestamp: new Date().toISOString(),
      });

      this.draft = "";
      this.isLoading = true;
      this.isStreaming = false;
      this.scrollToBottom();

      try {
        const payload = {
          userId: this.$store.getters.currentUser?._key || "anonymous",
          sessionId: this.sessionId || "widget-session",
          text: content,
          contextOption: "single-message",
          timestamp: new Date().toISOString(),
        };

        const response = await chatbotService.submitQuery(payload);

        const botMessage = {
          sender: "bot",
          content: "",
          _fullContent: response.response || "Lo siento, no pude generar una respuesta.",
          isTyping: true,
          queryId: response.queryId || null,
          timestamp: new Date().toISOString(),
        };

        this.messages.push(botMessage);
        this.isLoading = false;
        this.isStreaming = false;
        this.$nextTick(() => this.scrollToBottom());
        this.startTypewriter(botMessage);
      } catch (error) {
        console.error("Widget query error:", error);
        this.messages.push({
          sender: "bot",
          content: "Lo siento, hubo un error procesando tu mensaje. Intenta de nuevo.",
          timestamp: new Date().toISOString(),
        });
        this.isLoading = false;
        this.isStreaming = false;
        this.$nextTick(() => this.scrollToBottom());
      }
    },

    startTypewriter(botMessage) {
      if (this.typewriterTimer) {
        clearInterval(this.typewriterTimer);
      }
      const fullText = botMessage._fullContent;
      const msgIndex = this.messages.indexOf(botMessage);
      let charIndex = 0;
      const chunkSize = 3;
      const interval = 18;

      this.typewriterTimer = setInterval(() => {
        charIndex = Math.min(charIndex + chunkSize, fullText.length);
        const updated = { ...this.messages[msgIndex], content: fullText.substring(0, charIndex) };
        if (charIndex >= fullText.length) {
          updated.isTyping = false;
          delete updated._fullContent;
        }
        this.messages.splice(msgIndex, 1, updated);
        this.scrollToBottom();

        if (charIndex >= fullText.length) {
          clearInterval(this.typewriterTimer);
          this.typewriterTimer = null;
        }
      }, interval);
    },
    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (!container) return;
      container.scrollTop = container.scrollHeight;
    },

    async toggleRecording() {
      if (this.isRecording) {
        this.stopRecording();
      } else {
        await this.startRecording();
      }
    },

    async startRecording() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.audioChunks = [];
        this.mediaRecorder = new MediaRecorder(stream);

        this.mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) this.audioChunks.push(e.data);
        };

        this.mediaRecorder.onstop = async () => {
          stream.getTracks().forEach((t) => t.stop());
          const blob = new Blob(this.audioChunks, { type: 'audio/webm' });
          await this.transcribeAudio(blob);
        };

        this.mediaRecorder.start();
        this.isRecording = true;
      } catch (err) {
        console.error('Microphone access denied:', err);
      }
    },

    stopRecording() {
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
      }
      this.isRecording = false;
    },

    async transcribeAudio(blob) {
      this.isTranscribing = true;
      try {
        const formData = new FormData();
        formData.append('file', blob, 'audio.webm');
        const sttUrl = process.env.VUE_APP_STT_URL || '/stt/transcribe';
        const response = await fetch(sttUrl, { method: 'POST', body: formData });
        if (!response.ok) throw new Error(`STT error: ${response.status}`);
        const data = await response.json();
        if (data.text) {
          this.draft = (this.draft ? this.draft + ' ' : '') + data.text;
        }
      } catch (err) {
        console.error('Transcription failed:', err);
      } finally {
        this.isTranscribing = false;
      }
    },
  },
};
</script>

<style scoped>
.floating-chat-widget {
  position: fixed;
  right: 24px;
  bottom: 22px;
  z-index: 7000;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.launcher-btn {
  position: relative;
  width: 62px;
  height: 62px;
  border-radius: 50%;
  border: 2px solid #C9A027;
  background: #ffffff;
  box-shadow: 0 12px 30px rgba(13, 37, 80, 0.25);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.launcher-btn:hover {
  box-shadow: 0 14px 36px rgba(13, 37, 80, 0.38);
  transform: scale(1.06);
}

.launcher-btn:hover .launcher-tooltip {
  opacity: 1;
  transform: translateX(-50%) translateY(-6px);
}

.launcher-logo {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  object-fit: contain;
}

.launcher-tooltip {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%) translateY(0px);
  background: #0d2550;
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.chat-panel {
  width: 370px;
  max-width: calc(100vw - 26px);
  height: 500px;
  max-height: calc(100vh - 110px);
  background: #ffffff;
  border: 1px solid #d7deea;
  border-radius: 14px;
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.26);
  display: flex;
  flex-direction: column;
  margin-bottom: 14px;
  overflow: hidden;
}

.chat-panel-header {
  height: 64px;
  padding: 0 14px;
  border-bottom: 1px solid #e6ebf4;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8fafc;
}

.chat-title-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: contain;
  border: 2px solid #C9A027;
  background: #ffffff;
  padding: 2px;
}

.chat-title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.chat-subtitle {
  margin: 1px 0 0;
  font-size: 11px;
  color: #64748b;
}

.close-btn {
  border: none;
  background: transparent;
  color: #334155;
  font-size: 16px;
  cursor: pointer;
  width: 28px;
  height: 28px;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  background: #f1f5f9;
  padding: 14px;
}

.message {
  display: flex;
  margin-bottom: 10px;
}

.message.user {
  justify-content: flex-end;
}

.message.bot {
  justify-content: flex-start;
}

.bubble {
  max-width: 84%;
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.45;
  box-shadow: 0 3px 10px rgba(15, 23, 42, 0.08);
}

.message.user .bubble {
  background: #1e4d8c;
  color: #ffffff;
  border-bottom-right-radius: 4px;
}

.message.bot .bubble {
  background: #ffffff;
  color: #0f172a;
  border: 1px solid #d9e2f0;
  border-bottom-left-radius: 4px;
}

.bubble :deep(ul),
.bubble :deep(ol) {
  list-style-position: inside;
  padding-left: 4px;
  margin: 4px 0;
}

.bubble :deep(li) {
  margin-bottom: 2px;
}

.bubble :deep(p) {
  margin: 4px 0;
}

.typing-row {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #ffffff;
  border: 1px solid #d9e2f0;
  border-radius: 999px;
  padding: 8px 10px;
}

.typing-dot {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: #94a3b8;
  animation: pulse 1.1s infinite;
}

.typing-dot:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-dot:nth-child(3) {
  animation-delay: 0.4s;
}

/* ── Input row action buttons ────────────────────────────────── */
.action-btn {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: background 0.18s ease, transform 0.12s ease;
}

.action-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Send — gold fill, matching the classic send-btn accent */
.send-btn {
  background: #C9A027;
  color: #ffffff;
}

.send-btn:not(:disabled):hover {
  background: #a8841f;
  transform: scale(1.06);
}

/* Mic — navy outline, matches the classic mic-btn */
.mic-btn {
  background: #f1f5f9;
  color: #0d1b2e;
  border: 1.5px solid #cbd5e0;
}

.mic-btn:not(:disabled):hover {
  background: #e2e8f0;
  transform: scale(1.06);
}

.mic-btn.recording {
  background: #fee2e2;
  color: #dc2626;
  border-color: #dc2626;
  animation: mic-pulse 1s infinite;
}

.mic-btn.transcribing {
  background: #fef9c3;
  color: #92400e;
  border-color: #fbbf24;
}

@keyframes mic-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.35); }
  50%       { box-shadow: 0 0 0 5px rgba(220, 38, 38, 0); }
}

.chat-input-row {
  padding: 10px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  gap: 8px;
  background: #ffffff;
}

.chat-input {
  flex: 1;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
  outline: none;
}

.chat-input:focus {
  border-color: #1e4d8c;
  box-shadow: 0 0 0 3px rgba(30, 77, 140, 0.16);
}

/* old .send-btn block removed — now handled by .action-btn + .send-btn above */

.widget-pop-enter-active,
.widget-pop-leave-active {
  transition: all 0.2s ease;
}

.widget-pop-enter-from,
.widget-pop-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(0.98);
}

@keyframes pulse {
  0%,
  80%,
  100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.typing-cursor {
  display: inline-block;
  font-weight: 300;
  color: #1e4d8c;
  animation: widget-blink 0.7s step-end infinite;
  margin-left: 1px;
}

@keyframes widget-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

@media (max-width: 768px) {
  .floating-chat-widget {
    right: 12px;
    bottom: 12px;
  }

  .chat-panel {
    width: calc(100vw - 24px);
    height: 70vh;
  }
}
</style>
